/* eslint-disable react/prop-types */
import moment from 'moment';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ChangeEventHandler, FocusEventHandler, KeyboardEventHandler } from 'react';
import type { RCInputProps } from './iRCTimePicker';

const Input = forwardRef<HTMLInputElement, RCInputProps>((props, ref) => {
    const {
        placeholder = '请选择时间',
        focusOnOpen,
        open,
        handleInputWidthChange,
        value,
        format = 'HH:mm:ss',
        locale,
        hourOptions,
        minuteOptions,
        secondOptions,
        disabledHours,
        disabledMinutes,
        disabledSeconds,
        onChange,
        allowEmpty,
        defaultOpenValue,
        showHour,
        showMinute,
        showSecond,
        onBlur,
        blurHandle,
        autoComplete,
        autoFocus,
        prefixCls,
        readOnly,
        onFocus,
        disabled,
        name,
        id,
        fieldid,
        onEsc,
        onKeyDown
    } = props;

    const [str, setStr] = useState('');
    const [valueString, setValueString] = useState('');
    const [invalid, setInvalid] = useState(false);
    const refInput = useRef<HTMLInputElement>(null);

    // 暴露 ref 给父组件
    useImperativeHandle(ref, () => refInput.current!, []);

    // componentDidMount
    useEffect(() => {
        if (focusOnOpen) {
            const requestAnimationFrame = window.requestAnimationFrame || window.setTimeout;
            requestAnimationFrame(() => {
                refInput.current?.focus();
                refInput.current?.select();
            });
        }
        if (open) {
            const inputWidth = parseInt(refInput.current?.getBoundingClientRect().width + '');
            handleInputWidthChange?.(inputWidth); // 获取Input的宽度，传给父组件
        }
    }, [open]);

    // UNSAFE_componentWillReceiveProps
    useEffect(() => {
        setStr((value && value.format(format)) || '');
        setInvalid(false); // 从panel点击过来，获得值，Invalid就是false了
    }, [value, format]);

    // componentDidUpdate for locale
    useEffect(() => {
        if (locale) {
            moment.locale(locale.lang);
        }
    }, [locale]);

    function getProtoValue() {
        return ((value && moment(value)) || defaultOpenValue).clone();
    }

    const onInputChange: ChangeEventHandler<HTMLInputElement> = event => {
        let str = event.target.value;
        setStr(str);
        if (str) {
            setValueString(str);
            const valueObj = getProtoValue(); // 获取原值
            const parsed = moment(str, format, true); // 现在的时间值对象
            if (!parsed.isValid()) {
                setInvalid(true);
                return;
            }
            valueObj.hour(parsed.hour()).minute(parsed.minute()).second(parsed.second());
            // if time value not allowed, response warning.
            if (
                hourOptions.indexOf(valueObj.hour()) < 0 ||
                minuteOptions.indexOf(valueObj.minute()) < 0 || // 输入的不在提供的时间范围内
                secondOptions.indexOf(valueObj.second()) < 0
            ) {
                setInvalid(true);
                return;
            }
            // if time value is disabled, response warning.
            const disabledHourOptions = disabledHours();
            const disabledMinuteOptions = disabledMinutes(valueObj.hour());
            const disabledSecondOptions = disabledSeconds(valueObj.hour(), valueObj.minute());
            if (
                (disabledHourOptions && disabledHourOptions.indexOf(valueObj.hour()) >= 0) ||
                (disabledMinuteOptions && disabledMinuteOptions.indexOf(valueObj.minute()) >= 0) ||
                (disabledSecondOptions && disabledSecondOptions.indexOf(valueObj.second()) >= 0)
            ) {
                setInvalid(true);
                return;
            }
            if (value) {
                if (
                    value.hour() !== valueObj.hour() ||
                    value.minute() !== valueObj.minute() || // 如果和原来的值不同了
                    value.second() !== valueObj.second()
                ) {
                    const changedValue = value.clone();
                    changedValue.hour(valueObj.hour());
                    changedValue.minute(valueObj.minute());
                    changedValue.second(valueObj.second());
                    onChange(changedValue); // 重新设置value
                }
            } else {
                onChange(valueObj); // 没有的话，直接当成value
            }
        } else if (allowEmpty) {
            setValueString('');
            onChange(null); // 允许为空
        } else {
            setInvalid(true); // 不合法又不允许为空，就非法
            setValueString('');
            return;
        }
        setInvalid(false);
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
        if (e.keyCode === 27 || e.keyCode === 13) {
            onEsc?.(); // 快捷键关闭
        }

        onKeyDown?.(e);
    };

    const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
        let str = e.target.value || valueString;
        if (!str) {
            setInvalid(true);
            setStr('');
            blurHandle?.()
            return;
        }
        const strArr = str.split(':');
        const getMaxHour = (str: string) => {
            if (isNaN(+str)) {
                return '00';
            }
            if (+str > 23) {
                return 23;
            } else {
                return str.length === 1 ? '0' + str : str;
            }
        };
        const getMaxMinSec = (str: string) => {
            if (isNaN(+str)) {
                return '00';
            }
            if (+str > 59) {
                return 59;
            } else {
                return str.length === 1 ? '0' + str : str;
            }
        };
        if (strArr.length === 1) {
            switch (str.length) {
                case 1:
                    if (showHour) {
                        str = getMaxHour(str) + ':00:00';
                    } else if (showMinute) {
                        str = '00:' + getMaxMinSec(str) + ':00';
                    } else if (showSecond) {
                        str = '00:00:' + getMaxMinSec(str);
                    }
                    break;
                case 2:
                    if (showHour) {
                        str = getMaxHour(str) + ':00:00';
                    } else if (showMinute) {
                        str = '00:' + getMaxMinSec(str) + ':00';
                    } else if (showSecond) {
                        str = '00:00:' + getMaxMinSec(str);
                    }
                    break;
                case 3:
                    if (showHour) {
                        str =
                            str.substring(0, 2) + ':' + (showMinute ? getMaxMinSec(str.substring(2, 3)) : '00') + ':00';
                    } else if (showMinute) {
                        str =
                            '00:' +
                            getMaxMinSec(str.substring(0, 2)) +
                            ':' +
                            (showSecond ? getMaxMinSec(str.substring(2, 3)) : '00');
                    } else if (showSecond) {
                        str = '00:00:' + getMaxMinSec(str.substring(0, 2));
                    }
                    break;
                case 4:
                    if (showHour) {
                        str =
                            str.substring(0, 2) + ':' + (showMinute ? getMaxMinSec(str.substring(2, 4)) : '00') + ':00';
                    } else if (showMinute) {
                        str =
                            '00:' +
                            getMaxMinSec(str.substring(0, 2)) +
                            ':' +
                            (showSecond ? getMaxMinSec(str.substring(2, 4)) : '00');
                    } else if (showSecond) {
                        str = '00:00:' + getMaxMinSec(str.substring(0, 2));
                    }
                    break;
                case 5:
                    if (showHour) {
                        str =
                            getMaxHour(str.substring(0, 2)) +
                            ':' +
                            (showMinute ? getMaxMinSec(str.substring(2, 4)) : '00') +
                            ':' +
                            (showSecond ? getMaxMinSec(str.substring(4, 5)) : '00');
                    } else if (showMinute) {
                        str =
                            '00:' +
                            getMaxMinSec(str.substring(0, 2)) +
                            ':' +
                            (showSecond ? getMaxMinSec(str.substring(2, 4)) : '00');
                    } else if (showSecond) {
                        str = '00:00:' + getMaxMinSec(str.substring(0, 2));
                    }
                    break;
                case 6:
                    if (showHour) {
                        str =
                            getMaxHour(str.substring(0, 2)) +
                            ':' +
                            (showMinute ? getMaxMinSec(str.substring(2, 4)) : '00') +
                            ':' +
                            (showSecond ? getMaxMinSec(str.substring(4, 6)) : '00');
                    } else if (showMinute) {
                        str =
                            '00:' +
                            getMaxMinSec(str.substring(0, 2)) +
                            ':' +
                            (showSecond ? getMaxMinSec(str.substring(2, 4)) : '00');
                    } else if (showSecond) {
                        str = '00:00:' + getMaxMinSec(str.substring(0, 2));
                    }
                    break;
                default:
                    // 超过6位数字时，使用前6位数字
                    if (showHour) {
                        str =
                            getMaxHour(str.substring(0, 2)) +
                            ':' +
                            (showMinute ? getMaxMinSec(str.substring(2, 4)) : '00') +
                            ':' +
                            (showSecond ? getMaxMinSec(str.substring(4, 6)) : '00')
                    } else if (showMinute) {
                        str =
                            '00:' +
                            getMaxMinSec(str.substring(0, 2)) +
                            ':' +
                            (showSecond ? getMaxMinSec(str.substring(2, 4)) : '00')
                    } else if (showSecond) {
                        str = '00:00:' + getMaxMinSec(str.substring(0, 2))
                    }
                    break
            }
        }
        const date = moment(str, 'HH:mm:ss');
        const valueObj = getProtoValue(); // 获取原值
        valueObj.hour(date.hour()).minute(date.minute()).second(date.second());
        if (
            hourOptions.indexOf(date.hour()) < 0 ||
            minuteOptions.indexOf(date.minute()) < 0 || // 输入的不在提供的时间范围内
            secondOptions.indexOf(date.second()) < 0
        ) {
            setStr(value ? value.format(format) : '');
            setInvalid(false);
            blurHandle?.()
            return;
        }

        // if time value is disabled, response warning.
        const disabledHourOptions = disabledHours();
        const disabledMinuteOptions = disabledMinutes(date.hour());
        const disabledSecondOptions = disabledSeconds(date.hour(), date.minute());
        if (
            (disabledHourOptions && disabledHourOptions.indexOf(date.hour()) >= 0) ||
            (disabledMinuteOptions && disabledMinuteOptions.indexOf(date.minute()) >= 0) ||
            (disabledSecondOptions && disabledSecondOptions.indexOf(date.second()) >= 0)
        ) {
            setStr(value ? value.format(format) : '');
            setInvalid(false);
            blurHandle?.()
            return;
        }
        setStr(date.format(format));
        setInvalid(false);
        onBlur?.(date);
    }

    function getInput() {
        const invalidClass = invalid ? `${prefixCls}-input-invalid` : '';
        let newStr = str;
        if (format && format.includes(' a')) {
            if (typeof newStr === 'string') {
                let newWord = newStr.split(" ")
                if (newWord[1]) {
                    let toSpa = newWord[1].toLowerCase()
                    newStr = newWord[0] + ' ' + toSpa
                }
            }
        } else if (format && format.includes(' A')) {
            if (typeof newStr === 'string') {
                let newWord = newStr.split(" ")
                if (newWord[1]) {
                    let toSpa = newWord[1].toUpperCase()
                    newStr = newWord[0] + ' ' + toSpa
                }
            }
        }

        return (
            <input
                name={name}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                className={`${prefixCls}-input ${invalidClass}`}
                disabled={disabled}
                ref={refInput}
                id={id}
                fieldid={fieldid ? fieldid + '_input' : undefined}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                onBlur={handleBlur}
                value={newStr}
                title={newStr}
                placeholder={placeholder}
                onChange={onInputChange}
                readOnly={!!readOnly} // 将之前Header组件的input逻辑放到这里
            />
        );
    }

    return <div className={`${prefixCls}-input-wrap`}>{getInput()}</div>;
});

Input.displayName = 'Input';

export default Input;
